import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  ArchiveIcon,
  ArrowLeft,
  CalendarIcon,
  Check,
  Layers,
  Loader2,
  RotateCcw,
  RotateCw,
  UserPlus,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Header } from "@/components/Header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { rememberSpace, rememberedSpace } from "@/lib/activeSpace";
import { triggerSync } from "@/lib/board";
import { isAdmin } from "@/lib/perms";
import {
  archivePreviewOptions,
  membersQueryOptions,
  qk,
  settingsQueryOptions,
  spacesQueryOptions,
  useUser,
  usersQueryOptions,
} from "@/lib/queries";
import {
  applyArchiveCutoff,
  parseCutoffDate,
  restoreAllArchived,
  saveSettings,
} from "@/lib/settings";
import { ROLE_LABELS, type Role } from "@/lib/session";
import { createUser } from "@/lib/users";

const YMD = "yyyy-MM-dd";

export function Settings() {
  const user = useUser();
  const queryClient = useQueryClient();
  const admin = isAdmin(user);

  const { data: settings } = useQuery({
    ...settingsQueryOptions,
    enabled: admin,
  });
  const { data: users = [] } = useQuery({
    ...usersQueryOptions,
    enabled: admin,
  });
  const { data: members = [] } = useQuery({
    ...membersQueryOptions,
    enabled: admin,
  });
  const { data: spaces = [] } = useQuery({
    ...spacesQueryOptions,
    enabled: admin,
  });

  // The cutoff is per-space. Seed the section from the active selection the
  // viewer carries app-wide (URL ?space_id → remembered → first space) once
  // spaces load; the admin can re-target it without leaving Settings.
  const search = useSearch({ strict: false }) as { space_id?: string };
  const [archiveSpaceId, setArchiveSpaceId] = useState("");
  useEffect(() => {
    if (archiveSpaceId) return;
    const initial = search.space_id ?? rememberedSpace() ?? spaces[0]?.id ?? "";
    if (initial) setArchiveSpaceId(initial);
  }, [archiveSpaceId, search.space_id, spaces]);
  const archiveSpaceName =
    spaces.find((s) => s.id === archiveSpaceId)?.name ?? null;

  const [signoffDays, setSignoffDays] = useState("5");
  const [kickbackDays, setKickbackDays] = useState("7");
  const [newMemberId, setNewMemberId] = useState("");
  const [newRole, setNewRole] = useState<Role>("developer");

  // Seed the editable inputs once the saved thresholds load.
  useEffect(() => {
    if (settings) {
      setSignoffDays(String(settings.signoffStaleDays));
      setKickbackDays(String(settings.kickbackStaleDays));
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      saveSettings({
        signoffStaleDays: Number(signoffDays),
        kickbackStaleDays: Number(kickbackDays),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(settingsQueryOptions.queryKey, updated);
      void queryClient.invalidateQueries({ queryKey: qk.board });
      toast.success("Settings saved", { icon: <Check className="size-4" /> });
    },
    onError: (e) =>
      toast.error("Couldn't save settings", {
        description: e instanceof Error ? e.message : "Please try again.",
      }),
  });

  const autoSync = useMutation({
    mutationFn: (enabled: boolean) => saveSettings({ autoSyncEnabled: enabled }),
    onSuccess: (updated) => {
      queryClient.setQueryData(settingsQueryOptions.queryKey, updated);
      toast.success(
        updated.autoSyncEnabled
          ? "Automatic sync enabled"
          : "Automatic sync disabled",
        { icon: <Check className="size-4" /> },
      );
    },
    onError: (e) =>
      toast.error("Couldn't update automatic sync", {
        description: e instanceof Error ? e.message : "Please try again.",
      }),
  });

  const sync = useMutation({
    mutationFn: triggerSync,
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: qk.board });
      toast.success("Synced from ClickUp", {
        icon: <RotateCw className="size-4" />,
        description: `${r.created} new, ${r.reprojected} re-projected, ${r.unchanged} unchanged.`,
      });
    },
    onError: (e) =>
      toast.error("Sync failed", {
        description: e instanceof Error ? e.message : "Please try again.",
      }),
  });

  // Members not already added to Greenlight.
  const availableMembers = members.filter(
    (m) => !users.some((u) => u.clickupUserId === m.id),
  );

  const addUser = useMutation({
    mutationFn: () => {
      const member = members.find((m) => m.id === newMemberId);
      if (!member) throw new Error("Select a member");
      return createUser({
        clickupUserId: member.id,
        name: member.username,
        email: member.email,
        avatarUrl: member.avatar,
        role: newRole,
      });
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: qk.users });
      setNewMemberId("");
      setNewRole("developer");
      toast.success(`Added ${created.name ?? "user"}`, {
        icon: <UserPlus className="size-4" />,
      });
    },
    onError: (e) =>
      toast.error("Couldn't add user", {
        description: e instanceof Error ? e.message : "Please try again.",
      }),
  });

  // --- Archive backlog (reversible, date-driven) ---------------------------
  // Default the picker to today; if a cutoff was already applied, open on it.
  const [cutoff, setCutoff] = useState<Date>(() => new Date());
  useEffect(() => {
    // Seed the picker from a previously-applied cutoff when there is one. Parse
    // defensively — a non-date-only value must never make `cutoff` an Invalid
    // Date, or `format(cutoff, …)` below throws and crashes the whole page.
    const applied = parseCutoffDate(settings?.archiveCutoffDate);
    if (applied) setCutoff(applied);
  }, [settings?.archiveCutoffDate]);
  const cutoffYmd = format(cutoff, YMD);

  const { data: preview, isFetching: previewing } = useQuery({
    ...archivePreviewOptions(cutoffYmd, archiveSpaceId),
    enabled: admin && !!archiveSpaceId,
  });

  const invalidateAfterArchive = () => {
    void queryClient.invalidateQueries({ queryKey: qk.board });
    void queryClient.invalidateQueries({ queryKey: qk.archivePreview });
    void queryClient.invalidateQueries({ queryKey: qk.settings });
  };

  const archive = useMutation({
    mutationFn: () => applyArchiveCutoff(cutoffYmd, archiveSpaceId),
    onSuccess: ({ archived, restored }) => {
      invalidateAfterArchive();
      toast.success(`Archived ${archived} task${archived === 1 ? "" : "s"}`, {
        icon: <ArchiveIcon className="size-4" />,
        description:
          restored > 0
            ? `${restored} brought back into review.`
            : "Cleared off the board — reversible from here anytime.",
      });
    },
    onError: (e) =>
      toast.error("Archive failed", {
        description: e instanceof Error ? e.message : "Please try again.",
      }),
  });

  const restore = useMutation({
    mutationFn: () => restoreAllArchived(archiveSpaceId),
    onSuccess: ({ restored }) => {
      invalidateAfterArchive();
      toast.success(
        `Restored ${restored} task${restored === 1 ? "" : "s"} to review`,
        { icon: <RotateCcw className="size-4" /> },
      );
    },
    onError: (e) =>
      toast.error("Restore failed", {
        description: e instanceof Error ? e.message : "Please try again.",
      }),
  });

  const busy = archive.isPending || restore.isPending;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main className="mx-auto max-w-6xl px-6 py-8">
        <Link
          to="/"
          className="mb-4 inline-flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to board
        </Link>
        <h1 className="text-xl font-semibold">Settings</h1>

        {!admin ? (
          <p className="mt-6 text-sm text-muted-foreground">
            No settings available for your role.
          </p>
        ) : (
          <div className="mt-6 flex flex-col gap-4">
            <section className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <h2 className="text-sm font-medium">Urgency thresholds</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                How long a card can sit before it's flagged stale.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="signoff">
                    Pending review stale after (days)
                  </Label>
                  <Input
                    id="signoff"
                    type="number"
                    min={1}
                    value={signoffDays}
                    onChange={(e) => setSignoffDays(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="kickback">
                    Changes requested stale after (days)
                  </Label>
                  <Input
                    id="kickback"
                    type="number"
                    min={1}
                    value={kickbackDays}
                    onChange={(e) => setKickbackDays(e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={() => save.mutate()} disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </section>

            {/* Team members */}
            <section className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <h2 className="text-sm font-medium">Team members</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Who can sign in to Greenlight using ClickUp, and their role.
              </p>

              <ul className="mt-4 divide-y divide-border/60">
                {users.map((u) => (
                  <li
                    key={u.id}
                    className="flex items-center gap-3 py-2.5 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{u.name ?? "—"}</p>
                      {u.email && (
                        <p className="truncate text-xs text-muted-foreground">
                          {u.email}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {ROLE_LABELS[u.role]}
                    </span>
                    {u.status !== "active" && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        {u.status}
                      </span>
                    )}
                  </li>
                ))}
                {users.length === 0 && (
                  <li className="py-2.5 text-sm text-muted-foreground">
                    No users yet.
                  </li>
                )}
              </ul>

              <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-end">
                <div className="grid flex-1 gap-2">
                  <Label>Add a workspace member</Label>
                  <Select value={newMemberId} onValueChange={setNewMemberId}>
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={
                          availableMembers.length === 0
                            ? "Everyone is already added"
                            : "Select a member"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {availableMembers.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.username ?? m.email ?? m.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2 sm:w-44">
                  <Label>Role</Label>
                  <Select
                    value={newRole}
                    onValueChange={(v) => setNewRole(v as Role)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(ROLE_LABELS) as Role[]).map((r) => (
                        <SelectItem key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={() => addUser.mutate()}
                  disabled={!newMemberId || addUser.isPending}
                  className="shrink-0"
                >
                  {addUser.isPending ? "Adding…" : "Add user"}
                </Button>
              </div>
            </section>

            <div className="border-t border-border pt-8">
              <h2 className="mb-4 text-base font-semibold">Backlog</h2>
              <section className="rounded-xl border border-border bg-card p-5 shadow-sm">
                <h3 className="text-sm font-medium">Archive by cutoff date</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Archives tasks completed before the chosen date in the selected
                  space (pending review only), clearing them off the board.
                  Reversible at any time.
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Select
                    value={archiveSpaceId}
                    onValueChange={(v) => {
                      setArchiveSpaceId(v);
                      rememberSpace(v);
                    }}
                  >
                    {/* Grow the value so it left-aligns next to the icon (like
                        the date field), instead of centering between the icon
                        and chevron. Styled via the trigger because Radix's
                        Select.Value drops className. */}
                    <SelectTrigger className="w-[14rem] *:data-[slot=select-value]:flex-1">
                      <Layers className="size-4 text-muted-foreground" />
                      <SelectValue placeholder="Select space" />
                    </SelectTrigger>
                    <SelectContent>
                      {spaces.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name ?? s.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-[16rem] justify-start font-normal"
                      >
                        <CalendarIcon className="size-4" />
                        Completed before {format(cutoff, "MMM d, yyyy")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={cutoff}
                        onSelect={(d) => d && setCutoff(d)}
                        disabled={{ after: new Date() }}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <p className="text-sm text-muted-foreground" aria-live="polite">
                    {previewing || !preview ? (
                      "Calculating…"
                    ) : (
                      <>
                        <span className="font-medium text-foreground">
                          {preview.toArchive}
                        </span>{" "}
                        will be archived
                        {archiveSpaceName && (
                          <>
                            {" "}
                            in{" "}
                            <span className="font-medium text-foreground">
                              {archiveSpaceName}
                            </span>
                          </>
                        )}{" "}
                        ·{" "}
                        <span className="font-medium text-foreground">
                          {preview.remainingActive}
                        </span>{" "}
                        {preview.remainingActive === 1 ? "task" : "tasks"} will
                        remain on the board
                        {preview.toRestore > 0 && (
                          <> · {preview.toRestore} brought back</>
                        )}
                      </>
                    )}
                  </p>
                </div>

                <div className="mt-5 flex items-center justify-between gap-4 border-t border-border pt-4">
                  <div className="text-xs text-muted-foreground">
                    {preview && preview.cutoffArchived > 0 ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => restore.mutate()}
                        disabled={busy}
                        className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <RotateCcw className="size-3.5" />
                        {restore.isPending
                          ? "Restoring…"
                          : `Restore ${preview.cutoffArchived} cutoff-archived task${preview.cutoffArchived === 1 ? "" : "s"}`}
                      </Button>
                    ) : (
                      <span>No cutoff-archived tasks in this space.</span>
                    )}
                  </div>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        disabled={busy || !preview || preview.toArchive === 0}
                        className="shrink-0"
                      >
                        <ArchiveIcon className="size-4" />
                        {archive.isPending ? "Archiving…" : "Archive"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Archive {preview?.toArchive ?? 0} task
                          {preview?.toArchive === 1 ? "" : "s"}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Every pending task completed before{" "}
                          {format(cutoff, "MMMM d, yyyy")} moves off the board.
                          {preview && preview.toRestore > 0 && (
                            <>
                              {" "}
                              {preview.toRestore} previously-archived task
                              {preview.toRestore === 1 ? "" : "s"} now after this
                              date will come back.
                            </>
                          )}{" "}
                          This is reversible — you can move the date or restore
                          everything later.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          variant="destructive"
                          onClick={() => archive.mutate()}
                        >
                          Archive
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </section>
            </div>

            <div className="border-t border-border pt-8">
              <h2 className="mb-4 text-base font-semibold">Sync</h2>
              <section className="rounded-xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-medium">Automatic sync</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Periodically imports newly completed tasks from ClickUp and
                      notifies reviewers when a task enters review.
                    </p>
                  </div>
                  <Switch
                    aria-label="Automatic sync"
                    checked={settings?.autoSyncEnabled ?? false}
                    disabled={!settings || autoSync.isPending}
                    onCheckedChange={(v) => autoSync.mutate(v)}
                    className="shrink-0"
                  />
                </div>

                <div className="mt-5 flex items-center justify-between gap-4 border-t border-border pt-4">
                  <div>
                    <h3 className="text-sm font-medium">Sync now</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Imports completed tasks from ClickUp immediately. Reviewers
                      are not notified.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => sync.mutate()}
                    disabled={sync.isPending}
                    className="shrink-0 gap-2"
                  >
                    {sync.isPending ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Syncing…
                      </>
                    ) : (
                      <>
                        <RotateCw className="size-4" />
                        Sync now
                      </>
                    )}
                  </Button>
                </div>
              </section>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Settings;
